import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Button,
  Card,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import {
  AssignmentReturned as AssignmentReturnedIcon,
  Close as CloseIcon
} from '@material-ui/icons';

import { ApiParams } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import UserNeedAssignDesignInputDialog from '../dialogs/UserNeedAssignDesignInputDialog';
import UnlinkConfirmDialog from '../dialogs/UnlinkConfirmDialog';
import {
  useFindDesignInputsByUserNeedId,
  useProductEntityPrefix
} from '../Product.hooks';
import { BaseProductUserNeedsCardProps } from '../Product.interface';
import { getProductEntityName } from '../Product.config';

const UserNeedDesignInputReferenceCard: React.FC<BaseProductUserNeedsCardProps> =
  (props: BaseProductUserNeedsCardProps) => {
    const {
      productId,
      productUserNeed,
      productUserNeedVersion,
      onUpdate,
      onError
    } = props;
    const history = useHistory();
    const [openDialog, setOpenDialog] = useState(false);
    const [itemToUnlink, setItemToUnlink] = useState<number | undefined>();
    const [order, setOrder] = useState<string | undefined>();
    const [orderDirection, setOrderDirection] = useState<
      'ASC' | 'DESC' | undefined
    >();
    const {
      productDesignInputs,
      isLoadingProductDesignInputs,
      loadingProductDesignInputsError,
      findProductDesignInputs
    } = useFindDesignInputsByUserNeedId(productId, productUserNeed.id);

    const { isFieldDisabled } = useTaskEntityFields(productUserNeed.task);
    const prefix: string = useProductEntityPrefix(
      ProductBoardColumnEntity.DesignInput
    );

    const handleSort = (column: string) => {
      setOrder(column);
      setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
    };

    const getSortDirection = (column: string): 'asc' | 'desc' => {
      return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
    };

    const findProductDesignInputsWithParams = useCallback(() => {
      const params: ApiParams = {};
      if (order) params.order = order;
      if (orderDirection) params.direction = orderDirection;
      if (productUserNeedVersion)
        params.version = productUserNeedVersion.version;
      findProductDesignInputs(params);
    }, [
      findProductDesignInputs,
      order,
      orderDirection,
      productUserNeedVersion
    ]);

    useEffect(() => {
      findProductDesignInputsWithParams();
    }, [findProductDesignInputsWithParams]);

    useEffect(() => {
      if (loadingProductDesignInputsError) {
        onError?.(loadingProductDesignInputsError);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingProductDesignInputsError]);
    const disabled =
      Boolean(productUserNeedVersion) || isFieldDisabled('assignDesignInput');
    return (
      <Card>
        <CardTitle>
          <AssignmentReturnedIcon />
          {getProductEntityName('designInput')} Reference
        </CardTitle>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={24}>
                  <TableSortLabel
                    active={order === 'productDesignInput.id'}
                    direction={getSortDirection('productDesignInput.id')}
                    onClick={() => handleSort('productDesignInput.id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'productDesignInput.title'}
                    direction={getSortDirection('productDesignInput.title')}
                    onClick={() => handleSort('productDesignInput.title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'task.status'}
                    direction={getSortDirection('task.status')}
                    onClick={() => handleSort('task.status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingProductDesignInputs && (
                <TableRow>
                  <TableCell colSpan={3} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {productDesignInputs ? (
                productDesignInputs.map(designInput => (
                  <TableRow
                    key={designInput.id}
                    hover
                    onClick={() => {
                      history.push(
                        `/portal/products/${productId}/design-inputs/${designInput.id}`
                      );
                    }}
                  >
                    <TableCell>{prefix + designInput.id}</TableCell>
                    <WrappedTableCell>{designInput.title}</WrappedTableCell>
                    <TableCell>
                      <TaskStatusLabel task={designInput.task} />
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: 'center'
                      }}
                    >
                      {!disabled && (
                        <CloseIcon
                          onClick={event => {
                            event.stopPropagation();
                            setItemToUnlink(designInput.id);
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    No {getProductEntityName('designInput')}
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell colSpan={2}>
                  <Button
                    variant='outlined'
                    color='primary'
                    disabled={disabled}
                    onClick={() => setOpenDialog(true)}
                  >
                    Assign {getProductEntityName('designInput')}
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        {openDialog && (
          <UserNeedAssignDesignInputDialog
            open={openDialog}
            productId={productId}
            userNeedId={productUserNeed.id}
            selectedItems={productDesignInputs}
            onError={onError}
            onCancel={() => setOpenDialog(false)}
            onAssigned={() => {
              findProductDesignInputsWithParams();
              onUpdate?.();
              setOpenDialog(false);
            }}
          />
        )}
        <UnlinkConfirmDialog
          open={!!itemToUnlink}
          productId={productId}
          entityId={productUserNeed.id}
          from={ProductBoardColumnEntity.UserNeed}
          to={ProductBoardColumnEntity.DesignInput}
          unlinkItemId={itemToUnlink}
          selectedItems={productDesignInputs}
          onError={onError}
          onCancel={() => setItemToUnlink(undefined)}
          onChange={() => {
            setItemToUnlink(undefined);
            findProductDesignInputsWithParams();
            onUpdate?.();
          }}
        />
      </Card>
    );
  };

export default UserNeedDesignInputReferenceCard;
